import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import AutocompleteInput from '../AutocompleteInput';
import { reverseGeocode } from '../../lib/reverseGeocode';
import geocode from '../../lib/geocode';
const OnboardingStep1 = ({
  onNext,
  userData,
  setUserData
}) => {
  const [name, setName] = useState(userData.name || '');
  const [location, setLocation] = useState(userData.location || '');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  useEffect(() => {
    // Auto-detect location on component mount if not already set
    if (!location) {
      autoDetectLocation();
    }
  }, []);
  const autoDetectLocation = async () => {
    setIsLoadingLocation(true);
    setLocationError('');
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser');
      }
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true
        });
      });
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };

      // Try to get location name via reverse geocoding
      try {
        const locationName = await reverseGeocode(coords.latitude, coords.longitude);
        if (locationName) {
          setLocation(locationName);
          setUserData(prev => ({
            ...prev,
            coordinates: coords,
            location: locationName
          }));
        } else {
          throw new Error('Reverse geocoding failed');
        }
      } catch (reverseError) {
        // Fallback to coordinates string if reverse geocoding fails
        const coordsString = `${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`;
        setLocation(coordsString);
        setUserData(prev => ({
          ...prev,
          coordinates: coords,
          location: coordsString
        }));
      }
    } catch (error) {
      console.log('Location detection failed:', error.message);
      setLocationError('Unable to detect location automatically. Please search for your city below.');
    } finally {
      setIsLoadingLocation(false);
    }
  };
  const handleNext = () => {
    if (name.trim() && location.trim()) {
      setUserData(prev => ({
        ...prev,
        name: name.trim(),
        location: location.trim()
      }));
      onNext();
    }
  };
  const handleLocationSelect = async locationData => {
    // locationData comes from Open-Meteo geocoding API
    const selectedLocation = locationData.name;
    const coordinates = {
      latitude: locationData.latitude,
      longitude: locationData.longitude
    };
    setLocation(selectedLocation);
    setUserData(prev => ({
      ...prev,
      location: selectedLocation,
      coordinates: coordinates
    }));
    setLocationError('');
  };
  const handleManualLocationEntry = async inputLocation => {
    // If user types and presses enter without selecting from dropdown
    if (inputLocation.trim() && inputLocation !== location) {
      try {
        const coords = await geocode(inputLocation.trim());
        setLocation(inputLocation.trim());
        setUserData(prev => ({
          ...prev,
          location: inputLocation.trim(),
          coordinates: coords
        }));
        setLocationError('');
      } catch (error) {
        setLocationError('Location not found. Please try a different city name.');
      }
    }
  };
  return <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="text-center mb-6">
        <h2 className="text-h2 text-foreground mb-2">Welcome!</h2>
        <p className="text-body-sm text-muted-foreground">Lets start with basics</p>
      </div>
      
      <div className="space-y-5">
        <div className="rounded-3xl">
          <label className="block text-label text-foreground mb-2">
            What's your name?
          </label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" className="w-full p-3 rounded-lg border border-input 
                     bg-background text-foreground
                     focus:ring-2 focus:ring-ring focus:border-ring 
                     transition-colors duration-200" autoFocus />
        </div>

        <div>
          <label className="block text-label text-foreground mb-2">
            Your location
          </label>
          
          {/* Show current location if detected */}
          {location && !isLoadingLocation && <div className="mb-2 p-2 border border-green-200 dark:border-green-800 rounded-3xl bg-green-200 dark:bg-green-800">
              <div className="flex items-center text-body-sm text-green-700 dark:text-green-300">
                <span className="mr-2">📍</span>
                <span className="flex-1">{location}</span>
                <button onClick={() => {
              setLocation('');
              setUserData(prev => ({
                ...prev,
                location: '',
                coordinates: null
              }));
            }} className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 ml-2">
                  ✕
                </button>
              </div>
            </div>}
          
          {/* AutocompleteInput for searching locations */}
          {(!location || locationError) && <div className="relative">
              <AutocompleteInput onLocationSelect={handleLocationSelect} theme="light" // Will be handled by the component's dark mode detection
          placeholder={isLoadingLocation ? "Detecting location..." : "Search for your city"} disabled={isLoadingLocation} onManualEntry={handleManualLocationEntry} />
              {isLoadingLocation && <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>}
            </div>}
          
          {locationError && <p className="text-destructive text-caption mt-1">
              {locationError}
            </p>}
          
          {!location && !isLoadingLocation && <button onClick={autoDetectLocation} className="text-primary hover:text-primary/80 
                       text-caption mt-2 transition-colors duration-200 flex items-center">
              <span className="mr-1">📍</span>
              Detect my location automatically
            </button>}
        </div>

        <Button onClick={handleNext} disabled={!name.trim() || !location.trim()} className="w-full mt-6 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium py-3 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-3xl">
          Next →
        </Button>
      </div>
    </div>;
};
export default OnboardingStep1;